import { startCase } from "lodash";
import { Icon } from "semantic-ui-react";

import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import { createDateTimeFormatter, getPercentage } from "components/helpers";
import { EMAIL_SERVICES } from "constants/Constants";
import { EmailScheduleTableActions } from "routes/campaign/components/email_schedule_table/EmailScheduleTableActions";
import { CONSTANT_CONTACT_SYNC_STATUS } from "hooks/useEmailScheduleTable";

const emailServicesParsed = Object.keys(EMAIL_SERVICES).map(key => ({
  key: EMAIL_SERVICES[key],
  value: EMAIL_SERVICES[key],
  text: startCase(key.toLowerCase()),
}));

const formatDate = tz => createDateTimeFormatter({ timeZone: tz });

// CC and BCC arrive from the API as one comma-separated string, not a list.
const renderRecipientList = (value, scheduleId, prefix) => {
  const recipients =
    value
      ?.split(",")
      .map(recipient => recipient.trim())
      .filter(Boolean) ?? [];

  return (
    <ScrollColumn>
      {recipients.map((recipient, index) => (
        <li key={`${prefix}-${scheduleId}-${index}`}>{recipient}</li>
      ))}
    </ScrollColumn>
  );
};

export const getEmailScheduleTableColumns = ({
  campaignId,
  handleSyncData,
  invalidateData,
  handleDeleteEmailSchedule,
}) => {
  return [
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      width: 60,
      cell: ({ row }) => (
        <p style={{ fontWeight: "bold" }}>{row.original?.id}</p>
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "schedule_name",
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "status",
      cell: ({ row }) => {
        const emailService = row.original?.email_service;
        const status = row.original?.status;
        return emailService === EMAIL_SERVICES["CONSTANT_CONTACT"] ? (
          "N/A"
        ) : (
          <Icon
            name={status ? "check" : "close"}
            color={status ? "green" : "red"}
          />
        );
      },
    },
    {
      header: "Provider",
      id: "Provider",
      accessorKey: "email_service",
      cell: ({ row }) => {
        const emailService = row.original?.email_service;
        return (
          emailServicesParsed.find(p => p.value === emailService)?.text || "N/A"
        );
      },
    },
    {
      header: "Delivered Time & Date",
      id: "Delivered Time & Date",
      cell: ({ row }) => {
        const emailDeliveryTime = row.original?.email_delivery_time;
        const timeZone = row.original?.time_zone;
        return formatDate(timeZone)(emailDeliveryTime);
      },
    },
    {
      header: "Template",
      id: "Template",
      accessorKey: "email_template_name",
      cell: ({ row }) => {
        const {
          email_template_id,
          email_template_name,
          email_template_deleted,
        } = row.original;
        if (email_template_deleted) {
          return `${email_template_name} (deleted)`;
        }
        if (!email_template_id) {
          return email_template_name ?? null;
        }
        return (
          <TableLink
            href={`/settings/email_templates/${email_template_id}`}
            openInNewTab
          >
            {email_template_name}
          </TableLink>
        );
      },
    },
    {
      header: "From Name",
      id: "From Name",
      accessorKey: "sender_name",
    },
    {
      header: "From Address",
      id: "From Address",
      accessorKey: "info.from_address",
    },
    {
      header: "CC",
      id: "CC",
      accessorKey: "info.cc",
      enableSorting: false,
      cell: ({ row }) =>
        renderRecipientList(row.original?.info?.cc, row.original?.id, "cc"),
    },
    {
      header: "BCC",
      id: "BCC",
      accessorKey: "info.bcc",
      enableSorting: false,
      cell: ({ row }) =>
        renderRecipientList(row.original?.info?.bcc, row.original?.id, "bcc"),
    },
    {
      header: "# Sent",
      id: "# Sent",
      accessorKey: "metrics.sent",
    },
    {
      header: "# Delivered",
      id: "# Delivered",
      accessorKey: "metrics.delivered",
    },
    {
      header: "Bounce Rate",
      id: "Bounce Rate",
      accessorKey: "metrics.bounce_rate",
      cell: ({ row }) => {
        const metrics = row.original?.metrics;
        if (metrics?.delivered !== undefined) {
          return (
            <div>
              {metrics.sent === 0
                ? "0.0%"
                : getPercentage(metrics.bounced, metrics.sent)}
            </div>
          );
        } else {
          return "N/A";
        }
      },
    },
    {
      header: "Opened",
      id: "Opened",
      accessorKey: "metrics.unique_opened",
    },
    {
      header: "Clicks",
      id: "Clicks",
      accessorKey: "metrics.unique_clicked",
    },
    {
      header: "Open Rate",
      id: "Open Rate",
      accessorKey: "metrics.open_rate",
      cell: ({ row }) => {
        const metrics = row.original?.metrics;
        if (metrics?.unique_opened !== undefined) {
          return (
            <div>{getPercentage(metrics.unique_opened, metrics.delivered)}</div>
          );
        } else {
          return "N/A";
        }
      },
    },
    {
      header: "Click Rate",
      id: "Click Rate",
      accessorKey: "metrics.click_rate",
      cell: ({ row }) => {
        const metrics = row.original?.metrics;
        if (metrics?.unique_clicked !== undefined) {
          return (
            <div>
              {getPercentage(metrics.unique_clicked, metrics.unique_opened)}
            </div>
          );
        }
        return "N/A";
      },
    },
    {
      header: "Unsubscribed",
      id: "Unsubscribed",
      accessorKey: "metrics.unsubscribed",
    },
    {
      header: "Sync Status",
      id: "Sync Status",
      accessorKey: "info.sync_status",
      cell: ({ row }) => {
        const isConstantContact =
          row.original?.email_service === EMAIL_SERVICES.CONSTANT_CONTACT;

        if (!isConstantContact) {
          return "N/A";
        }

        const syncStatus = row.original?.info?.sync_status;
        // for old rows without sync_status, verify last_synced_at is not null
        if (!syncStatus && row.original?.info?.last_synced_at) {
          return CONSTANT_CONTACT_SYNC_STATUS.completed;
        }
        return syncStatus ? syncStatus : "N/A";
      },
    },
    {
      header: "Last Synced At",
      id: "Last Synced At",
      accessorKey: "info.last_synced_at",
      cell: ({ row }) => {
        const timeZone = row.original?.time_zone;
        const lastSyncedAt = row.original?.info?.last_synced_at;
        const isSyncing = row.original?.info?.syncing;

        return isSyncing
          ? "Syncing..."
          : timeZone && lastSyncedAt
            ? formatDate(timeZone)(lastSyncedAt)
            : "N/A";
      },
    },
    {
      header: "CC AM",
      id: "CC AM",
      accessorKey: "info.cc_account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <Icon
          name={row.original?.info?.cc_account_managers ? "check" : "close"}
          color={row.original?.info?.cc_account_managers ? "green" : "red"}
        />
      ),
    },
    {
      header: "CC Ext",
      id: "CC Ext",
      accessorKey: "info.cc_external_account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <Icon
          name={
            row.original?.info?.cc_external_account_managers ? "check" : "close"
          }
          color={
            row.original?.info?.cc_external_account_managers ? "green" : "red"
          }
        />
      ),
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <EmailScheduleTableActions
          campaignId={campaignId}
          original={row.original}
          handleSyncData={handleSyncData}
          invalidateData={invalidateData}
          handleDeleteEmailSchedule={handleDeleteEmailSchedule}
        />
      ),
    },
  ];
};
